/** The password and the sessions of the signed-in person - card C-A, gate 30, on My profile
 * (ADR-0212: the person's own pages are the avatar menu's, My profile and My settings).
 *
 * Two panels, both acting on the caller and neither role-gated:
 *
 * **Change password.** The current password is asked for, and the three ways it can go wrong are
 * three different sentences beside the field they are about:
 *
 * - *the current password is wrong* (`403 current_password_incorrect`) - **beside the current
 *   field**, and the person stays signed in. It is a 403 and not a 401 because the client treats a
 *   401 as *the session is over* and swaps in the sign-in form, which is the one remedy that cannot
 *   help somebody who mistyped a field;
 * - *the new one is too weak* (`422 password_too_weak`) - **beside the new field, printing the
 *   server's own sentence, which is the rule**. The rule is the server's (`passwords.hash_password`)
 *   and is not repeated here: a second copy of it is the one that goes stale;
 * - *the credential is locked* - said where the current password is, with the time to wait.
 *
 * The two passwords are held in component state only and cleared on success. Nothing here writes
 * either to storage, and the mutation does not keep them (`gcTime: 0`).
 *
 * **It signs every other session of yours out and keeps this one** (S2-04): a password is usually
 * changed because it may have been stolen, and a stolen cookie must not survive that.
 *
 * **Sessions.** Your live ones in this workspace, newest first, the one you are using marked and
 * **not offered for ending** - that is signing out, which has its own control. Ending another signs
 * that browser out on its next request.
 */

import { useState } from "react";
import type { FormEvent } from "react";

import { useChangePassword, useProfileSessions, useReader, instant, useRevokeSession, useSignIns } from "../api/profile";
import type { ProfileSession, SignIn } from "../api/profile";
import { Refused } from "../api/refusal";
import { FieldError } from "../components/FieldError";
import { LazyDetails } from "../components/ui";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { reasonWords, securityEventWords } from "../lib/customerWords";
import own from "./Profile.module.css";
import styles from "./Screen.module.css";

/** Which input a password refusal is about. An unrecognised code maps to nothing: a form that
 * guessed a field for a code it has never met would point at the wrong input. */
type PasswordField = "current" | "new";

const FIELD_OF_REFUSAL: Readonly<Record<string, PasswordField>> = {
  current_password_incorrect: "current",
  credential_locked: "current",
  password_too_weak: "new",
};

function refusedField(error: unknown): PasswordField | null {
  return error instanceof Refused ? (FIELD_OF_REFUSAL[error.code] ?? null) : null;
}

export function PasswordPanel() {
  const change = useChangePassword();
  const [current, setCurrent] = useState("");
  const [replacement, setReplacement] = useState("");
  const [repeat, setRepeat] = useState("");
  const [done, setDone] = useState(false);

  const field = refusedField(change.error);
  // Checked here because it is a typo guard and not a rule of the product: the server cannot know
  // what the person meant to type twice.
  const mismatch = replacement !== "" && repeat !== "" && replacement !== repeat;
  const [checks, attach] = useFormChecks();
  // The server's refusals (a wrong current password, a weak new one) move focus to their input.
  useFocusRefusal(checks.element, change.error);

  function submit(event: FormEvent) {
    event.preventDefault();
    setDone(false);
    // The button stays enabled: pressing it says what is missing beside each box (G-P8-03).
    const ok = checks.run([
      { field: "current", ok: current !== "", message: "Type your current password." },
      { field: "new", ok: replacement !== "", message: "Choose a new password." },
      { field: "repeat", ok: repeat !== "", message: "Type the new password again." },
      { field: "repeat", ok: !mismatch, message: "The two new passwords are not the same." },
    ]);
    if (!ok) return;
    change.mutate(
      { currentPassword: current, newPassword: replacement },
      {
        onSuccess: () => {
          setCurrent("");
          setReplacement("");
          setRepeat("");
          setDone(true);
        },
      },
    );
  }

  return (
    <form ref={attach} className={own.form} onSubmit={submit} noValidate data-testid="password-form">
      <label className={own.field}>
        Current password
        <input
          type="password"
          value={current}
          autoComplete="current-password"
          {...checks.field("current")}
          aria-invalid={field === "current" || checks.errors.current !== undefined ? true : undefined}
          data-testid="password-current"
          onChange={(event) => {
            checks.touch("current");
            setCurrent(event.target.value);
            setDone(false);
          }}
        />
        <FieldError {...checks.say("current")} />
      </label>
      {field === "current" ? (
        <p className={own.refused} role="alert" data-testid="password-current-refused">
          {change.error?.message}
        </p>
      ) : null}

      <label className={own.field}>
        New password
        <input
          type="password"
          value={replacement}
          autoComplete="new-password"
          {...checks.field("new")}
          aria-invalid={field === "new" || checks.errors.new !== undefined ? true : undefined}
          data-testid="password-new"
          onChange={(event) => {
            checks.touch("new");
            setReplacement(event.target.value);
            setDone(false);
          }}
        />
        <FieldError {...checks.say("new")} />
      </label>
      {field === "new" ? (
        // The server's sentence, verbatim: it states the rule.
        <p className={own.refused} role="alert" data-testid="password-too-weak">
          {change.error?.message}
        </p>
      ) : null}

      <label className={own.field}>
        Repeat the new password
        <input
          type="password"
          value={repeat}
          autoComplete="new-password"
          {...checks.field("repeat")}
          aria-invalid={mismatch || checks.errors.repeat !== undefined ? true : undefined}
          data-testid="password-repeat"
          onChange={(event) => {
            checks.touch("repeat");
            setRepeat(event.target.value);
            setDone(false);
          }}
        />
        <FieldError {...checks.say("repeat")} />
      </label>
      {/* Said live as a typo guard; once the button has been pressed the same sentence is the
          field's own (`FieldError`), so it is not printed twice. */}
      {mismatch && checks.errors.repeat === undefined ? (
        <p className={own.refused} role="alert" data-testid="password-mismatch">
          The two new passwords are not the same.
        </p>
      ) : null}

      {/* Anything that is not about a field: a dropped connection, a stale session, a 500. */}
      {field === null ? <WriteFailed error={change.error} of="your password" /> : null}

      {done && !change.isPending ? (
        <p className={own.saved} role="status" data-testid="password-changed">
          Your password has been changed. Every other session of yours has been signed out; this
          one stays.
        </p>
      ) : null}

      <span>
        <button
          type="submit"
          className={styles.primary}
          disabled={change.isPending}
          data-testid="password-save"
        >
          {change.isPending ? "Changing…" : "Change password"}
        </button>
      </span>
    </form>
  );
}

function SessionRow({
  session,
  onEnd,
  pending,
}: {
  session: ProfileSession;
  onEnd: (id: string) => void;
  pending: boolean;
}) {
  const reader = useReader();
  return (
    <tr className={own.sessionRow} data-testid="session" data-current={session.current ? "yes" : "no"}>
      <td>Signed in {instant(session.created_at, reader)}</td>
      <td>Last active {instant(session.last_seen_at, reader)}</td>
      <td>{session.ip_prefix ? `Network ${session.ip_prefix}` : ""}</td>
      <td className={own.sessionAct}>
        {session.current ? (
          <span className={own.thisOne} data-testid="session-current">
            This session
          </span>
        ) : (
          <button
            type="button"
            className={styles.fixLink}
            disabled={pending}
            data-testid="session-revoke"
            onClick={() => onEnd(session.id)}
          >
            End this session
          </button>
        )}
      </td>
    </tr>
  );
}

function SignInRow({ entry }: { entry: SignIn }) {
  const reader = useReader();
  return (
    <li className={own.historyRow} data-testid="sign-in" data-event-type={entry.event_type}>
      <span>{instant(entry.created_at, reader)}</span>
      <span>{securityEventWords(entry.event_type)}</span>
      <span>{entry.reason ? reasonWords(entry.reason) : ""}</span>
      <span>{entry.ip_prefix ? `Network ${entry.ip_prefix}` : ""}</span>
    </li>
  );
}

/** The person's own recent sign-ins, failed attempts and lockouts (G-P8-07) - the page that answers
 * "has somebody been trying my account?". Newest first, the network and never an address; what each
 * row says is the server's record, not a summary of it. A list, not a table: the page's one table is
 * the live sessions. */
function SignInHistoryBody() {
  const signIns = useSignIns();
  if (!signIns.error && signIns.data?.length === 0) {
    return (
      <ul className={own.historyList} data-testid="sign-ins-empty" aria-label="Your recent sign-ins">
        <li className={own.historyRow}>No sign-ins recorded</li>
      </ul>
    );
  }
  return (
    <Loaded query={signIns} of="Your sign-in history">
      {(rows) => (
        <ul className={own.historyList} data-testid="sign-ins" aria-label="Your recent sign-ins">
          {rows.map((row) => (
            <SignInRow key={row.id} entry={row} />
          ))}
        </ul>
      )}
    </Loaded>
  );
}

/** Behind a disclosure: the history is a record to look up, not text the page leads with, and the
 * page is held to a word budget (a list of events is body text; a table's cells are not, but the page
 * keeps one table, the live sessions). Mounted when opened, so it is read when somebody asks. */
export function SignInHistoryPanel() {
  return (
    <LazyDetails summary="Recent sign-ins" testId="sign-in-history-details">
      <SignInHistoryBody />
    </LazyDetails>
  );
}

export function SessionsPanel() {
  const sessions = useProfileSessions();
  const revoke = useRevokeSession();

  return (
    <>
      <Loaded query={sessions} of="Your sessions" empty="You have no live sessions.">
        {(rows) => (
          <table className={own.sessionTable} data-testid="sessions" aria-label="Your live sessions">
            <tbody>
              {rows.map((row) => (
                <SessionRow
                  key={row.id}
                  session={row}
                  pending={revoke.isPending}
                  onEnd={(id) => revoke.mutate(id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </Loaded>
      <WriteFailed
        error={revoke.error}
        of="that session"
        onReload={() => {
          revoke.reset();
          void sessions.refetch();
        }}
      />
    </>
  );
}

